import { useLocalSearchParams } from 'expo-router'
import { MapPin } from 'lucide-react-native'
import { Dimensions, Image, StyleSheet, View } from 'react-native'
import { Card, Text } from 'react-native-paper'
import { BREAKPOINTS } from '@/lib/config'
import { formatLocationGeometry, getLocationThumbnailUrl } from '@/lib/locations'
import type { Location } from '@/types/locations'
import { Link } from '../Link'

const isSmallScreen = Dimensions.get('window').width < BREAKPOINTS.sm

interface LocationCardProps {
    location: Location
}

export function LocationCard({ location }: LocationCardProps) {
    const params = useLocalSearchParams<{ orgSlug: string; jobSlug: string }>()

    return (
        <Link
            href={`/${params.orgSlug}/settings/jobs/${params.jobSlug}/locations/${location.id}/edit`}
        >
            <Card style={styles.card}>
                <View style={[styles.cardContent, isSmallScreen && styles.cardContentSmall]}>
                    <LocationThumbnail location={location} />
                    <View style={styles.contentWrapper}>
                        <LocationInfo location={location} />
                        <LocationMeta location={location} />
                    </View>
                </View>
            </Card>
        </Link>
    )
}

function LocationThumbnail({ location }: { location: Location }) {
    const thumbnailUrl = getLocationThumbnailUrl(location, '96x72')

    if (thumbnailUrl) {
        return (
            <View
                style={[styles.thumbnailContainer, isSmallScreen && styles.thumbnailContainerSmall]}
            >
                <Image source={{ uri: thumbnailUrl }} style={styles.thumbnail} resizeMode="cover" />
            </View>
        )
    }

    // Fallback to icon if no thumbnail
    return (
        <View style={[styles.locationIcon, isSmallScreen && styles.locationIconSmall]}>
            <MapPin size={isSmallScreen ? 20 : 24} color="#3b82f6" />
        </View>
    )
}

function LocationInfo({ location }: { location: Location }) {
    const isInactive = location.inactive === true

    return (
        <View style={styles.locationInfo}>
            <View style={styles.locationHeader}>
                <Text
                    variant={isSmallScreen ? 'titleMedium' : 'headlineSmall'}
                    style={styles.locationName}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                >
                    {location.name}
                </Text>
                {isInactive && (
                    <Text variant="labelSmall" style={styles.inactiveBadge}>
                        Inactive
                    </Text>
                )}
            </View>

            {location.tags && location.tags.length > 0 && (
                <Text variant="labelSmall" style={styles.locationTag} numberOfLines={1}>
                    {location.tags[0]}
                </Text>
            )}

            {location.description && (
                <Text variant="bodySmall" style={styles.locationDescription} numberOfLines={1}>
                    {location.description}
                </Text>
            )}

            <Text variant="bodySmall" style={styles.locationGeometry} numberOfLines={1}>
                {formatLocationGeometry(location)}
            </Text>
        </View>
    )
}

function LocationMeta({ location }: { location: Location }) {
    const createdDate = location.created
        ? new Date(location.created).toLocaleDateString()
        : 'Unknown'
    const isInactive = location.inactive === true

    return (
        <View style={styles.locationMeta}>
            {!isInactive && (
                <Text variant="labelSmall" style={styles.locationStatus}>
                    Active
                </Text>
            )}
            <Text variant="bodySmall" style={styles.locationDate} numberOfLines={1}>
                {createdDate}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create({
    card: {
        margin: 0,
        padding: 12,
    },
    cardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    cardContentSmall: {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    contentWrapper: {
        flex: 1,
        flexDirection: 'row',
        gap: 8,
    },
    locationIcon: {
        width: 48,
        height: 48,
        backgroundColor: '#dbeafe',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        flexShrink: 0,
    },
    locationIconSmall: {
        width: 40,
        height: 40,
    },
    thumbnailContainer: {
        width: 80,
        height: 60,
        borderRadius: 8,
        overflow: 'hidden',
        elevation: 1,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 1,
        flexShrink: 0,
    },
    thumbnailContainerSmall: {
        width: '100%',
        height: 120,
        marginBottom: 8,
    },
    thumbnail: {
        width: '100%',
        height: '100%',
        borderRadius: 8,
    },
    locationInfo: {
        flex: 1,
        gap: 2,
        minWidth: 0,
    },
    locationHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
    },
    locationName: {
        fontWeight: '600',
        flex: 1,
        minWidth: 0,
    },
    locationTag: {
        color: '#6b7280',
        fontSize: 12,
    },
    inactiveBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 12,
        overflow: 'hidden',
        fontSize: 11,
    },
    locationDescription: {
        color: '#6b7280',
        fontSize: 13,
    },
    locationGeometry: {
        color: '#6b7280',
        fontSize: 12,
    },
    locationMeta: {
        alignItems: 'flex-end',
        gap: 2,
        justifyContent: 'center',
        flexShrink: 0,
    },
    locationStatus: {
        color: '#22c55e',
        fontWeight: '600',
        fontSize: 12,
    },
    locationDate: {
        color: '#9ca3af',
        fontSize: 11,
    },
})
